/**
 * Concrete order requests — "Review & submit your request".
 *
 * Concrete never goes through Shopify checkout. Any attempt to check out with
 * concrete in the cart opens this drawer instead, which posts the customer's
 * inputs to the Workflow Webhooks Flow trigger (X-Api-Key). The office confirms
 * availability and charges later.
 *
 * This file sends inputs only. It never sends a chargeable price.
 *
 * Plain script (not a module) so it can be loaded with `defer` from the
 * snippet and still intercept clicks anywhere on the page.
 */
(function () {
  'use strict';

  /* Cart line properties written by the PDP configurator. */
  var PROP_POUR = '01 · Select weekday or weekend pour';
  var PROP_MIX = '02 · Select Mix';
  var PROP_VOLUME = '03 · Volume (m³)';
  var PROP_DATE = '04 · Date';
  var PROP_WINDOW = '05 · Window';
  var PROP_DELIVERY_FEE = '07 · Delivery Fee';
  var PROP_DATE_ISO = '_pour_date_iso';
  var PROP_EST_TOTAL = '_est_total_display';

  /* Workflow Webhooks maps Flow fieldOne to this exact body key (leading space). */
  var FLOW_FIELD_KEY = ' {{fieldOne}}';

  var state = {
    cart: null,
    pours: [],
    idempotencyKey: null,
    submitting: false,
    completed: false,
  };

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  function drawer() {
    return document.querySelector('[data-request-drawer]');
  }

  function webhookConfig() {
    var root = drawer();
    return {
      endpoint: (root && root.getAttribute('data-endpoint')) || '',
      apiKey: (root && root.getAttribute('data-api-key')) || '',
    };
  }

  /**
   * Optionally gate requests behind a customer account. Controlled by the
   * "Require customers to sign in before requesting" theme setting, so it can be
   * turned off without a code change if it costs too many requests.
   */
  function loginGate() {
    var root = drawer();
    return {
      required: Boolean(root) && root.getAttribute('data-require-login') === 'true',
      loggedIn: Boolean(root) && root.getAttribute('data-logged-in') === 'true',
      url: (root && root.getAttribute('data-login-url')) || '/account/login',
    };
  }

  /** True when we sent them to sign in, so the caller should stop. */
  function redirectToLoginIfRequired() {
    var gate = loginGate();
    if (!gate.required || gate.loggedIn) return false;

    // Come back to the cart after signing in, so the pour is still waiting.
    var back = window.location.pathname + window.location.search;
    if (!/^\/cart/.test(back)) back = '/cart';
    var sep = gate.url.indexOf('?') === -1 ? '?' : '&';
    window.location.href = gate.url + sep + 'return_url=' + encodeURIComponent(back);
    return true;
  }

  /**
   * Cart drawer uses <dialog showModal()> under 990px. A fixed request overlay
   * sits behind that top layer and looks "stuck". Close it first.
   */
  function closeCartDrawer() {
    var cart = document.getElementById('cart-drawer');
    if (!cart || typeof cart.close !== 'function') return Promise.resolve();
    try {
      var result = cart.close();
      return result && typeof result.then === 'function' ? result : Promise.resolve();
    } catch (e) {
      return Promise.resolve();
    }
  }

  function revealDrawer(root) {
    if (!root) return;
    if (typeof root.showModal === 'function') {
      if (!root.open) root.showModal();
    } else {
      root.hidden = false;
    }
    document.documentElement.setAttribute('scroll-lock', '');
    document.body.style.overflow = 'hidden';
  }

  function concealDrawer(root) {
    if (!root) return;
    if (typeof root.close === 'function' && root.open) {
      try {
        root.close();
      } catch (e) {
        /* ignore */
      }
    }
    root.hidden = true;
    document.documentElement.removeAttribute('scroll-lock');
    document.body.style.overflow = '';
  }

  /** RFC 4122 v4. crypto.randomUUID is unavailable on older iOS Safari. */
  function uuidv4() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    var bytes = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    var hex = [];
    for (var i = 0; i < 16; i++) hex.push((bytes[i] + 0x100).toString(16).slice(1));
    return (
      hex.slice(0, 4).join('') + '-' +
      hex.slice(4, 6).join('') + '-' +
      hex.slice(6, 8).join('') + '-' +
      hex.slice(8, 10).join('') + '-' +
      hex.slice(10, 16).join('')
    );
  }

  /** Cart line properties arrive as an object, or an array in some responses. */
  function normalizeProperties(raw) {
    var out = {};
    if (!raw) return out;
    if (Array.isArray(raw)) {
      raw.forEach(function (entry) {
        if (entry && typeof entry === 'object' && typeof entry.name === 'string') {
          out[entry.name] = String(entry.value == null ? '' : entry.value);
        }
      });
      return out;
    }
    if (typeof raw !== 'object') return out;
    Object.keys(raw).forEach(function (key) {
      if (/^\d+$/.test(key)) return;
      var value = raw[key];
      if (value == null || value === '') return;
      out[key] = String(value);
    });
    return out;
  }

  /** A pour line is one the configurator stamped with a volume. */
  function isPourLine(item) {
    var props = normalizeProperties(item && item.properties);
    return Boolean(props[PROP_VOLUME]);
  }

  function pourLines(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    return cart.items.filter(isPourLine);
  }

  /** True when the cart holds anything that must not reach Shopify checkout. */
  function cartHasConcrete(cart) {
    return pourLines(cart).length > 0;
  }

  function parseVolume(text) {
    if (!text) return 0;
    var match = String(text).replace(/,/g, '').match(/([\d.]+)/);
    if (!match) return 0;
    var value = parseFloat(match[1]);
    return isFinite(value) ? value : 0;
  }

  function formatMoneyCents(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Shopify rejects a bare local number on draftOrderCreate — the whole request
   * dies with "Phone is invalid" — so every number is forced to E.164 (+61…)
   * before it leaves the browser.
   *
   * Returns '' when the number can't be read as an Australian one. The validator
   * treats that as a failure rather than sending something Shopify will reject.
   */
  function toE164AU(raw) {
    var s = String(raw == null ? '' : raw).trim();
    var hadPlus = s.charAt(0) === '+';
    var digits = s.replace(/\D/g, '');
    if (!digits) return '';

    if (hadPlus) {
      // Already international. Leave anything that isn't Australian alone.
      if (digits.indexOf('61') !== 0) return '+' + digits;
      digits = digits.slice(2);
    } else if (digits.indexOf('0061') === 0) {
      digits = digits.slice(4);
    } else if (digits.indexOf('61') === 0 && digits.length >= 11) {
      digits = digits.slice(2);
    }

    // Drop the national trunk zero: 0410… -> 410…
    if (digits.charAt(0) === '0') digits = digits.slice(1);

    // Australian subscriber numbers are nine digits (mobile 4xx, landline 2/3/7/8).
    if (digits.length !== 9) return '';

    return '+61' + digits;
  }

  function fetchCart() {
    return fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (res) {
      return res.json();
    });
  }

  // ---------------------------------------------------------------------------
  // Reading the pour out of the cart
  // ---------------------------------------------------------------------------

  /**
   * Build the request line from a cart line.
   *
   * Everything here is an input the customer already chose on the PDP. The
   * estimate is carried for display only and is never sent.
   */
  function pourFromCartLine(item) {
    var props = normalizeProperties(item.properties);
    var pourLabel = props[PROP_POUR] || '';
    var volume = parseVolume(props[PROP_VOLUME]);
    var estTotalCents = Math.round(parseFloat(props[PROP_EST_TOTAL] || '0') * 100);

    return {
      variantId: 'gid://shopify/ProductVariant/' + item.variant_id,
      volumeM3: volume,
      pourType: /weekend|saturday|sunday/i.test(pourLabel) ? 'weekend' : 'weekday',
      mix: props[PROP_MIX] || item.product_title || '',
      date: props[PROP_DATE_ISO] || '',
      window: props[PROP_WINDOW] || '',
      display: {
        pour: pourLabel,
        mix: props[PROP_MIX] || '',
        volume: props[PROP_VOLUME] || '',
        date: props[PROP_DATE] || '',
        window: props[PROP_WINDOW] || '',
        deliveryFee: props[PROP_DELIVERY_FEE] || '',
        estTotalCents: estTotalCents > 0 ? estTotalCents : item.line_price || 0,
      },
    };
  }

  /** One summary block per pour. Every pour in the cart goes on the request. */
  function renderSummaryBlock(pour, index, count) {
    var block = document.createElement('div');
    block.className = 'request-summary__pour';

    if (count > 1) {
      var title = document.createElement('p');
      title.className = 'request-summary__pour-title font-mono';
      title.textContent = 'POUR ' + (index + 1) + ' OF ' + count;
      block.appendChild(title);
    }

    var rows = [
      ['POUR', pour.display.pour],
      ['MIX', pour.display.mix],
      ['VOLUME', pour.display.volume],
      ['DATE', pour.display.date],
      ['WINDOW', pour.display.window],
    ];
    if (pour.display.deliveryFee) {
      rows.push(['MINIMUM LOAD FEE', pour.display.deliveryFee]);
    }

    var list = document.createElement('dl');
    list.className = 'request-summary__rows';
    rows.forEach(function (row) {
      if (!row[1]) return;
      var wrap = document.createElement('div');
      wrap.className = 'request-summary__row';

      var dt = document.createElement('dt');
      dt.className = 'request-summary__label font-mono';
      dt.textContent = row[0];

      var dd = document.createElement('dd');
      dd.className = 'request-summary__value';
      dd.textContent = row[1];

      wrap.appendChild(dt);
      wrap.appendChild(dd);
      list.appendChild(wrap);
    });

    block.appendChild(list);
    return block;
  }

  function renderSummary(pours) {
    var root = drawer();
    if (!root) return;

    var heading = root.querySelector('#request-summary-heading');
    if (heading) heading.textContent = pours.length > 1 ? 'YOUR POURS' : 'YOUR POUR';

    var totalCents = 0;
    var container = root.querySelector('[data-request-summary]');
    if (container) {
      container.innerHTML = '';
      pours.forEach(function (pour, index) {
        totalCents += pour.display.estTotalCents || 0;
        container.appendChild(renderSummaryBlock(pour, index, pours.length));
      });
    }

    var total = root.querySelector('[data-request-total]');
    if (total) {
      total.textContent = totalCents ? formatMoneyCents(totalCents) : '—';
    }

    var submit = root.querySelector('[data-request-submit]');
    if (submit) {
      submit.disabled = false;
      submit.setAttribute('aria-disabled', 'false');
      submit.classList.remove('request-form__submit--blocked');
    }
  }

  // ---------------------------------------------------------------------------
  // Field errors
  // ---------------------------------------------------------------------------

  function setError(field, message) {
    var root = drawer();
    if (!root) return;
    var el = root.querySelector('[data-request-error="' + field + '"]');
    if (el) {
      el.textContent = message || '';
      el.hidden = !message;
    }
    var input = root.querySelector('[name="' + field + '"]');
    if (input) input.classList.toggle('request-form__input--error', Boolean(message));
  }

  function clearErrors() {
    var root = drawer();
    if (!root) return;
    root.querySelectorAll('[data-request-error]').forEach(function (el) {
      el.textContent = '';
      el.hidden = true;
    });
    root.querySelectorAll('.request-form__input--error').forEach(function (el) {
      el.classList.remove('request-form__input--error');
    });
  }

  function valueOf(form, name) {
    var el = form.elements.namedItem(name);
    return el && typeof el.value === 'string' ? el.value.trim() : '';
  }

  /**
   * Client-side validation for fast feedback only. The server validates
   * everything again and is the authority.
   */
  function validate(form) {
    clearErrors();
    var ok = true;

    function fail(field, message) {
      setError(field, message);
      ok = false;
    }

    if (valueOf(form, 'name').length < 2) fail('name', 'Enter your full name.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(valueOf(form, 'email'))) {
      fail('email', 'Enter a valid email address.');
    }
    if (!toE164AU(valueOf(form, 'phone'))) {
      fail('phone', 'Enter an Australian mobile or landline, for example 0410 347 809.');
    }
    if (valueOf(form, 'address1').length < 3) {
      fail('address1', 'Enter the delivery street address.');
    }
    if (valueOf(form, 'suburb').length < 2) fail('suburb', 'Enter the suburb.');
    if (!valueOf(form, 'state')) fail('state', 'Select a state.');
    if (!/^\d{4}$/.test(valueOf(form, 'postcode'))) {
      fail('postcode', 'Enter a 4-digit postcode.');
    }

    return ok;
  }

  // ---------------------------------------------------------------------------
  // Open / close
  // ---------------------------------------------------------------------------

  function showStep(name) {
    var root = drawer();
    if (!root) return;
    root.querySelectorAll('[data-request-step]').forEach(function (step) {
      step.hidden = step.getAttribute('data-request-step') !== name;
    });
  }

  function openDrawer() {
    var root = drawer();
    if (!root) return;

    // Sign-in gate, when the theme setting asks for it.
    if (redirectToLoginIfRequired()) return;

    // Close cart immediately so mobile showModal() cannot cover the request UI.
    closeCartDrawer();

    fetchCart()
      .then(function (cart) {
        state.cart = cart;
        var lines = pourLines(cart);
        if (!lines.length) {
          // Nothing to request — send them to the cart rather than a blank drawer.
          window.location.href = '/cart';
          return;
        }

        state.pours = lines.map(pourFromCartLine);
        // A fresh key per opening, so a genuine second request is not
        // swallowed as a replay of the first.
        state.idempotencyKey = uuidv4();

        renderSummary(state.pours);
        clearErrors();
        showStep('form');

        revealDrawer(root);

        var firstField = root.querySelector('[name="name"]');
        if (firstField) {
          try {
            firstField.focus();
          } catch (e) {
            /* Focus can fail mid-dialog transition; non-fatal. */
          }
        }
      })
      .catch(function () {
        window.location.href = '/cart';
      });
  }

  function closeDrawer() {
    var root = drawer();
    if (!root) return;
    concealDrawer(root);

    // The cart was emptied when the request went in, so the cart count and
    // any cart page behind the drawer are now stale. Reload rather than leave
    // the customer looking at items they no longer have.
    if (state.completed) window.location.assign('/');
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  function setSubmitting(on) {
    state.submitting = on;
    var root = drawer();
    if (!root) return;
    var button = root.querySelector('[data-request-submit]');
    var label = root.querySelector('[data-request-submit-label]');
    if (button) button.disabled = on;
    if (label) label.textContent = on ? 'Submitting…' : 'Submit request';
  }

  function submitRequest(form) {
    // Guard the double-click in the browser as well as on the server.
    if (state.submitting) return;
    if (!state.pours.length) return;

    if (!validate(form)) {
      var firstError = form.querySelector('.request-form__input--error, [data-request-error]:not([hidden])');
      if (firstError && typeof firstError.scrollIntoView === 'function') {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    var config = webhookConfig();
    if (!config.endpoint || !config.apiKey) {
      setError('form', 'Request service unavailable. Please call the office on 07 5551 4446.');
      return;
    }

    setSubmitting(true);

    var requestRef = 'REQ-' + String(state.idempotencyKey || uuidv4()).replace(/-/g, '').slice(0, 8).toUpperCase();

    var payload = {
      idempotencyKey: state.idempotencyKey,
      reference: requestRef,
      hp: valueOf(form, 'company'),
      contact: {
        name: valueOf(form, 'name'),
        email: valueOf(form, 'email'),
        phone: toE164AU(valueOf(form, 'phone')),
      },
      delivery: {
        address1: valueOf(form, 'address1'),
        address2: valueOf(form, 'address2'),
        suburb: valueOf(form, 'suburb'),
        state: valueOf(form, 'state'),
        postcode: valueOf(form, 'postcode'),
        notes: valueOf(form, 'notes'),
      },
      lines: state.pours.map(function (pour) {
        return {
          variantId: pour.variantId,
          volumeM3: pour.volumeM3,
          pourType: pour.pourType,
          mix: pour.mix,
          date: pour.date,
          window: pour.window,
        };
      }),
    };

    // Strict Workflow Webhooks mapping: only the mapped key is allowed at the top level.
    // CORS allow-list is Content-Type / X-Api-Key / X-Signature — no custom Event-Id header.
    var body = {};
    body[FLOW_FIELD_KEY] = payload;

    fetch(config.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Api-Key': config.apiKey,
      },
      body: JSON.stringify(body),
    })
      .then(function (res) {
        return res
          .json()
          .catch(function () {
            return null;
          })
          .then(function (data) {
            return { ok: res.ok, status: res.status, data: data };
          });
      })
      .then(function (result) {
        if (!result.ok) {
          var err =
            (result.data && (result.data.message || (result.data.error && result.data.error.message) || result.data.error)) ||
            'We could not submit your request. Please call the office on 07 5551 4446.';
          setError('form', typeof err === 'string' ? err : 'We could not submit your request. Please call the office on 07 5551 4446.');
          setSubmitting(false);
          return;
        }

        // Flow webhook is async — confirmation uses the client reference.
        showConfirmation({ requestRef: requestRef }, payload.contact.email);

        return fetch('/cart/clear.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
        }).catch(function () {
          /* The request already succeeded; a failed clear is not the
             customer's problem. */
        });
      })
      .catch(function () {
        setError(
          'form',
          'We could not reach our system. Please check your connection, or call 07 5551 4446.'
        );
        setSubmitting(false);
      });
  }

  function showConfirmation(data, email) {
    var root = drawer();
    if (!root) return;

    var ref = root.querySelector('[data-request-ref]');
    if (ref) ref.textContent = data.requestRef || data.draftName || '—';

    var emailEl = root.querySelector('[data-request-email]');
    if (emailEl) emailEl.textContent = email;

    state.completed = true;
    showStep('done');
    setSubmitting(false);

    var body = root.querySelector('.request-drawer__body');
    if (body) body.scrollTop = 0;
  }

  // ---------------------------------------------------------------------------
  // Checkout interception
  // ---------------------------------------------------------------------------

  /**
   * Anything that would start Shopify checkout.
   *
   * Covers the cart page button, the cart drawer button, accelerated checkout
   * buttons and plain links to /checkout or a cart permalink.
   */
  function checkoutTriggerFrom(target) {
    if (!(target instanceof Element)) return null;

    var named = target.closest('[name="checkout"], [data-cart-terms-checkout]');
    if (named) return named;

    var link = target.closest('a[href]');
    if (link) {
      var href = link.getAttribute('href') || '';
      if (/^\/checkout|\/cart\/c\/|\/cart\/\d+:/.test(href)) return link;
    }

    return null;
  }

  function interceptCheckout(event) {
    var trigger = checkoutTriggerFrom(event.target);
    if (!trigger) return;

    // Reuse the cart we already have where possible; otherwise stop the
    // navigation first and decide once the cart has loaded.
    if (state.cart && !cartHasConcrete(state.cart)) return;

    event.preventDefault();
    event.stopPropagation();

    fetchCart().then(function (cart) {
      state.cart = cart;
      if (cartHasConcrete(cart)) {
        openDrawer();
      } else if (trigger instanceof HTMLAnchorElement) {
        window.location.href = trigger.href;
      } else if (trigger.form) {
        // No concrete after all — let the real checkout proceed.
        trigger.form.submit();
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Wiring
  // ---------------------------------------------------------------------------

  function onClick(event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-request-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (target.closest('[data-request-open]')) {
      event.preventDefault();
      openDrawer();
      return;
    }

    interceptCheckout(event);
  }

  function onSubmit(event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.matches('[data-request-form]')) {
      event.preventDefault();
      submitRequest(form);
      return;
    }

    // A cart form submitted by keyboard rather than a click still heads for
    // checkout, so it needs the same guard.
    var submitter = event.submitter;
    if (submitter && submitter.getAttribute('name') === 'checkout') {
      if (state.cart && !cartHasConcrete(state.cart)) return;
      event.preventDefault();
      fetchCart().then(function (cart) {
        state.cart = cart;
        if (cartHasConcrete(cart)) openDrawer();
        else form.submit();
      });
    }
  }

  /**
   * Rewrite the phone field to +61 form as soon as they leave it, so what they
   * see is what we send. Silent when the number isn't recognisable — validate()
   * is where that gets reported.
   */
  function onFocusOut(event) {
    var el = event.target;
    if (!(el instanceof Element)) return;
    if (el.getAttribute('name') !== 'phone') return;
    if (!el.closest || !el.closest('[data-request-form]')) return;
    var e164 = toE164AU(el.value);
    if (e164) el.value = e164;
  }

  function onKeydown(event) {
    if (event.key !== 'Escape') return;
    var root = drawer();
    if (!root) return;
    if (root.open || !root.hidden) closeDrawer();
  }

  function onDialogCancel(event) {
    // Native <dialog> Escape — keep our close path (and post-submit redirect).
    event.preventDefault();
    closeDrawer();
  }

  // Capture phase, so the theme's own cart handlers do not get there first.
  document.addEventListener('click', onClick, true);
  document.addEventListener('submit', onSubmit, true);
  document.addEventListener('focusout', onFocusOut, true);
  document.addEventListener('keydown', onKeydown);

  var root = drawer();
  if (root) {
    root.addEventListener('cancel', onDialogCancel);
  }

  // Warm the cart so the first checkout click does not wait on a round trip.
  fetchCart()
    .then(function (cart) {
      state.cart = cart;
    })
    .catch(function () {
      /* Non-fatal: the click handler fetches the cart again anyway. */
    });
})();
